import React from 'react';
import { X, ArrowRight, Check } from 'lucide-react';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPlan: 'vision' | 'anticipation' | 'securite';
  targetPlan: 'anticipation' | 'securite';
  blockedFeature: string;
}

const PLAN_INFO = {
  anticipation: {
    name: 'Pack Métier',
    price: '59€',
    tagline: 'CORE + outils métiers',
    description: 'Accédez aux modules métiers adaptés à votre secteur.',
    features: [
      'Toutes les fonctionnalités CORE',
      '1 module métier au choix',
      'Agriculture, Associations ou PME',
      'Gestion personnalisée',
      'Comptes bancaires illimités',
    ],
  },
  securite: {
    name: 'Pack Pilotage',
    price: '69€',
    tagline: 'Rapports automatisés',
    description: 'Obtenez des rapports financiers mensuels automatisés en PDF.',
    features: [
      'Toutes les fonctionnalités Métier',
      'Rapports financiers mensuels',
      'Génération PDF automatique',
      'Historique complet des rapports',
      'Tableaux de bord avancés',
      'Comptes bancaires illimités',
    ],
  },
};

export function UpgradeModal({ isOpen, onClose, currentPlan, targetPlan, blockedFeature }: UpgradeModalProps) {
  if (!isOpen) return null;

  const planInfo = PLAN_INFO[targetPlan];

  const handleUpgrade = () => {
    window.location.href = '/tarifs';
  };

  const getMessage = () => {
    if (targetPlan === 'anticipation') {
      return 'Accédez aux modules métiers personnalisés pour votre secteur : Agriculture, Associations ou PME.';
    }
    if (targetPlan === 'securite') {
      return 'Générez automatiquement vos rapports financiers mensuels avec tableaux de bord avancés et export PDF.';
    }
    return 'Cette fonctionnalité nécessite un pack supérieur.';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{planInfo.name}</h2>
              <p className="text-sm text-gray-500 mt-1">{planInfo.tagline}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-gray-700">{getMessage()}</p>
          </div>

          <div className="mb-6">
            <div className="flex items-baseline gap-2 mb-4">
              <span className="text-4xl font-bold text-gray-900">{planInfo.price}</span>
              <span className="text-gray-500">/mois</span>
            </div>
            <p className="text-gray-600">{planInfo.description}</p>
          </div>

          <div className="mb-8">
            <h3 className="font-semibold text-gray-900 mb-4">Fonctionnalités incluses :</h3>
            <ul className="space-y-3">
              {planInfo.features.map((feature, index) => (
                <li key={index} className="flex items-start gap-3">
                  <Check size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleUpgrade}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              Découvrir l'offre {planInfo.name}
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
